import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { playerService, User, FantasyTeam } from '../services/playerService';
import { useTheme } from '@mui/material/styles';

const LeaguePage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching } = useUser();
  const [leagues, setLeagues] = useState<any[]>([]); // Changed to any[] as FantasyLeague is removed
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [joinLeagueCode, setJoinLeagueCode] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Memoize the loadLeagueData function to prevent recreation on every render
  const loadLeagueData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 LeaguePage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ LeaguePage: User loaded:', user);
      setCurrentUser(user);

      // Load user's fantasy leagues
      const userLeagues = await playerService.getFantasyLeaguesByUser(user.id);
      console.log('✅ LeaguePage: User leagues loaded:', userLeagues);
      setLeagues(userLeagues);

    } catch (err) {
      console.error('❌ LeaguePage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load league data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadLeagueData();
    }
  }, [currentUsername, isUserSwitching, loadLeagueData]);

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="info">
          User not found. Please ensure you are logged in.
        </Alert>
      </Box>
    );
  }

  // Get primary league (first one)
  const primaryLeague = leagues[0];

  if (!primaryLeague) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="info">
          No fantasy league found. Please join a league first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* League Overview */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            🏆 League Overview
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography color="text.secondary">League Name:</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.name}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography color="text.secondary">Status:</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>{primaryLeague.status}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography color="text.secondary">Max Teams:</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.maxTeams}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography color="text.secondary">Scoring System:</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.scoringSystem}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography color="text.secondary">Draft Type:</Typography>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
              <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.draftType}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          Create New League
        </Button>
        <Button variant="outlined" startIcon={<GroupIcon />} onClick={() => setJoinDialogOpen(true)} sx={{ ml: 2 }}>
          Join League
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* League Standings */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 League Standings
              </Typography>
              
              {/* Standings Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography sx={{ flex: '0 0 40px', fontWeight: 'bold' }} color="primary.main">
                  #
                </Typography>
                <Typography sx={{ flex: 1, fontWeight: 'bold' }}>Team</Typography>
                <Typography sx={{ flex: '0 0 80px', fontWeight: 'bold', textAlign: 'center' }}>FPPG</Typography>
                <Typography sx={{ flex: '0 0 80px', fontWeight: 'bold', textAlign: 'center' }}>Players</Typography>
              </Box>
              
              {/* Standings Rows */}
              {leagues.length > 0 ? (
                leagues.map((league: any, index: number) => { // Changed to any as FantasyLeague is removed
                  // This part of the loop is not directly related to the original userLeagueStandings
                  // and will not render standings data.
                  return (
                    <Box key={league.id} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {league.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {league.status}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Max Teams: {league.maxTeams}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scoring: {league.scoringSystem}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography color="text.secondary">
                    No league data available yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* League Stats */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                📈 League Statistics
              </Typography>
              
              {primaryLeague ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Max Teams:</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.maxTeams}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Min Teams:</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.minTeams}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Scoring System:</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.scoringSystem}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Draft Type:</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.draftType}</Typography>
                  </Box>
                  
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
                      ℹ️ League Info
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {primaryLeague.name}
                    </Typography>
                    <Typography variant="caption" color="info.dark">
                      Created: {new Date(primaryLeague.createdDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No league data available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create League Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New League</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="League Name"
            type="text"
            fullWidth
            variant="standard"
            value={newLeagueName}
            onChange={(e) => setNewLeagueName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            // TODO: Implement league creation logic
            setSnackbarMessage('League created successfully!');
            setSnackbarOpen(true);
            setCreateDialogOpen(false);
          }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Join League Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)}>
        <DialogTitle>Join League</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="League Code"
            type="text"
            fullWidth
            variant="standard"
            value={joinLeagueCode}
            onChange={(e) => setJoinLeagueCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            // TODO: Implement league joining logic
            setSnackbarMessage('League joined successfully!');
            setSnackbarOpen(true);
            setJoinDialogOpen(false);
          }}>Join</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaguePage;
